import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';

import { TopBar } from '@app/shared/ui/organisms/Admin/Common';
import { NotFound } from '@app/shared/ui/molecules/Common';
import { InfoSection, BottomSection } from '@app/shared/ui/organisms/Admin/WithdrawalDetails';
import {
  useGetWithdrawalByIdQuery,
  useUpdateWithdrawalStatusMutation,
  useFindUserQuery,
} from '@app/store/slices/admin/adminApi';
import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import notification from '@app/shared/utils/notification';
import {
  WithdrawalStatus,
  WithdrawalReviewStatus,
} from '@app/store/slices/withdrawal/withdrawalTypes';
import { Page } from '@app/routes';
import { FindUserBy } from '@app/shared/types/admin/common';
import { LoadingSpinner } from '@app/shared/ui/atoms/Common';

export default function AdminWithdrawalDetailsPage() {
  const { id } = useParams();
  const withdrawalId = id ? Number(id) : undefined;

  const { data: withdrawalResponse, isLoading: isWithdrawalResponseLoading } =
    useGetWithdrawalByIdQuery(withdrawalId ?? skipToken);
  const withdrawal = withdrawalResponse?.withdrawal;
  const { data: user, isLoading: isUserLoading } = useFindUserQuery(
    withdrawal?.userId
      ? { value: String(withdrawal.userId), searchType: FindUserBy.USER_ID }
      : skipToken
  );
  const [updateWithdrawalStatus, { isLoading: isUpdatingWithdrawalStatus }] =
    useUpdateWithdrawalStatusMutation();
  const { handleFetchError } = useApiErrorHandler();
  const { t } = useTranslation();

  const handleWithdrawalStatusChange = async (status: WithdrawalReviewStatus) => {
    if (isUpdatingWithdrawalStatus || !withdrawalId) {
      return;
    }
    try {
      await updateWithdrawalStatus({ id: withdrawalId, status }).unwrap();
      notification.success(t('admin_withdrawal_details.change_status_success'));
    } catch (err: unknown) {
      handleFetchError(err);
    }
  };

  if (isWithdrawalResponseLoading || isUserLoading) {
    return <LoadingSpinner />;
  }

  if (!withdrawal) {
    return <NotFound />;
  }

  const userId = withdrawal.userId;
  const displayBottomSection = [WithdrawalStatus.PENDING, WithdrawalStatus.REVIEWING].includes(
    withdrawal.status
  );

  return (
    <div>
      <TopBar
        url={userId ? `${Page.ADMIN_USERS}/${String(userId)}` : undefined}
        translationKey="admin_withdrawal_details.user_info"
      />
      <InfoSection
        reviewer={{
          telegramUsername: withdrawal.reviewer?.telegramUsername,
        }}
        withdrawal={{
          status: withdrawal.status,
          amount: withdrawal.requestedAmount,
          fee: withdrawal.fee,
          total: withdrawal.payoutAmount,
          address: withdrawal.address,
        }}
        user={{
          telegramId: withdrawal.user?.telegramId,
          telegramUsername: withdrawal.user?.telegramUsername,
          fullName: user?.profile?.fullName,
        }}
      />
      {displayBottomSection && (
        <BottomSection
          status={withdrawal.status}
          onStatusChange={handleWithdrawalStatusChange}
          userBalance={user?.finance?.tonBalance}
          userFrozenBalance={user?.finance?.tonFrozenBalance}
        />
      )}
    </div>
  );
}
